import React, { useCallback, useRef } from 'react';

const isTouch = () => !!window.matchMedia?.('(pointer: coarse)').matches;

// Clue text input. On touch screens the first tap only focuses it, without the keyboard
// (inputmode "none"), so the suggestions open; tapping it again while they're open brings
// the keyboard up. With the suggestions closed (their × button), a tap reopens them.
const ClueField = React.forwardRef(({ suggestOpen, onRequestSuggest, onFocus, onBlur, ...props }, ref) => {
  const el = useRef(null);
  const typing = useRef(false);
  const refocus = useRef(false);     // this tap switched to typing; re-focus on click to show the keyboard
  const refocusing = useRef(false);  // inside that blur/focus pair

  const setRef = useCallback(node => {
    el.current = node;
    if (node && isTouch() && !typing.current) node.inputMode = 'none';
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  }, [ref]);

  const handlePointerDown = e => {
    if (e.pointerType === 'mouse' || !isTouch() || document.activeElement !== el.current) return;
    if (!suggestOpen) {
      onRequestSuggest?.();
    } else if (!typing.current) {
      typing.current = true;
      el.current.inputMode = 'text';
      refocus.current = true;
    }
  };

  // Keyboards only appear for a focus made inside the tap itself, so focus again here.
  const handleClick = () => {
    if (!refocus.current) return;
    refocus.current = false;
    refocusing.current = true;
    el.current.blur();
    el.current.focus();
    refocusing.current = false;
  };

  const handleBlur = e => {
    if (!refocusing.current) {
      typing.current = false;
      if (isTouch()) el.current.inputMode = 'none';
    }
    onBlur?.(e);
  };

  return (
    <input
      {...props}
      ref={setRef}
      type="text"
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      onFocus={onFocus}
      onBlur={handleBlur}
    />
  );
});

export default ClueField;
